
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const servicesRef = useRef<HTMLDivElement>(null);

  // Handle scroll for header transparency
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsServicesOpen(false);
    }
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      // Navigate to home first, then scroll
      window.location.href = `/#${sectionId}`;
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const isHomePage = location.pathname === '/';
  const isAtTop = !isScrolled && isHomePage;

  return (
    <header 
      className={`fixed top-0 left-0 w-full transition-all duration-220 ease-out ${isScrolled ? 'header--scrolled' : ''}`}
      style={{ 
        height: window.innerWidth <= 768 ? '60px' : '68px',
        backgroundColor: isAtTop ? 'rgba(255,255,255,0.06)' : '#FFFFFF',
        backdropFilter: isAtTop ? 'blur(8px)' : 'none',
        boxShadow: isAtTop ? 'none' : '0 6px 18px rgba(0,0,0,0.06)',
        zIndex: 1000
      }}
      role="navigation"
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-full flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <Link 
            to="/"
            className="transition-opacity duration-200 cursor-pointer hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A] rounded"
            style={{ marginRight: '20px' }}
          >
            <span 
              className="brand-text font-semibold"
              style={{ 
                color: isAtTop ? '#FFFFFF' : '#2B2B2B',
                textShadow: isAtTop ? '0 1px 2px rgba(0,0,0,0.45)' : 'none',
                fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                fontWeight: '600',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              NextPhase Workflows
            </span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-2">
          <Link 
            to="/" 
            className={`nav-link px-3.5 py-2 font-medium transition-all duration-180 cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A] ${
              isActive('/') ? 'nav-link--active' : ''
            }`}
            style={{ 
              color: isAtTop ? '#FFFFFF' : '#2B2B2B',
              textShadow: isAtTop ? '0 1px 2px rgba(0,0,0,0.45)' : 'none',
              fontSize: '16.5px',
              fontWeight: '500',
              letterSpacing: '0.01em',
              fontFamily: 'Inter, sans-serif',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center'
            }}
            aria-current={isActive('/') ? 'page' : undefined}
          >
            Home
          </Link>
          
          {/* Services Dropdown */}
          <div 
            ref={servicesRef}
            className="relative"
            onMouseEnter={() => setIsServicesOpen(true)}
            onMouseLeave={() => setIsServicesOpen(false)}
          >
            <button 
              className={`nav-link px-3.5 py-2 font-medium transition-all duration-180 cursor-pointer rounded flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A] ${
                isActive('/services') ? 'nav-link--active' : ''
              }`}
              style={{ 
                color: isAtTop ? '#FFFFFF' : '#2B2B2B',
                textShadow: isAtTop ? '0 1px 2px rgba(0,0,0,0.45)' : 'none',
                fontSize: '16.5px',
                fontWeight: '500',
                letterSpacing: '0.01em',
                fontFamily: 'Inter, sans-serif',
                minHeight: '40px'
              }}
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              onKeyDown={handleKeyDown}
              aria-expanded={isServicesOpen}
              aria-haspopup="true"
              aria-current={isActive('/services') ? 'page' : undefined}
            >
              Services
              <i 
                className={`ri-arrow-down-s-line ml-1 transition-transform duration-180 ${
                  isServicesOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>
            
            {/* Dropdown Menu */}
            <div 
              className={`absolute top-full left-0 w-72 bg-white rounded-xl shadow-lg border-0 py-2.5 transition-all duration-200 ${
                isServicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}
              style={{ 
                boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
                borderRadius: '14px',
                padding: '10px',
                zIndex: 1100,
                marginTop: '0',
                pointerEvents: isServicesOpen ? 'auto' : 'none'
              }}
              role="menu"
              aria-label="Services menu"
            >
              <Link 
                to="/services/workflow-system-architecture"
                className="dropdown-item block px-4 py-3 text-sm font-medium transition-all duration-180 cursor-pointer rounded-lg relative focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A]"
                style={{ 
                  color: '#2B2B2B', 
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14.5px',
                  fontWeight: '500'
                }}
                role="menuitem"
                onClick={() => setIsServicesOpen(false)}
              >
                Workflow System Architecture
              </Link>
              <Link 
                to="/services/intake-automation"
                className="dropdown-item block px-4 py-3 text-sm font-medium transition-all duration-180 cursor-pointer rounded-lg relative focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A]"
                style={{ 
                  color: '#2B2B2B', 
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14.5px',
                  fontWeight: '500'
                }}
                role="menuitem"
                onClick={() => setIsServicesOpen(false)}
              >
                Lead & Client Intake Systems
              </Link>
              <Link 
                to="/services/crm-automation"
                className="dropdown-item block px-4 py-3 text-sm font-medium transition-all duration-180 cursor-pointer rounded-lg relative focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A]"
                style={{ 
                  color: '#2B2B2B', 
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14.5px',
                  fontWeight: '500'
                }}
                role="menuitem"
                onClick={() => setIsServicesOpen(false)}
              >
                CRM Process Automation
              </Link>
              <Link 
                to="/services/concierge-ai"
                className="dropdown-item block px-4 py-3 text-sm font-medium transition-all duration-180 cursor-pointer rounded-lg relative focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A]"
                style={{ 
                  color: '#2B2B2B', 
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14.5px',
                  fontWeight: '500'
                }}
                role="menuitem"
                onClick={() => setIsServicesOpen(false)}
              >
                NextPhase Concierge AI™
              </Link>
              <Link 
                to="/services/how-we-work"
                className="dropdown-item block px-4 py-3 text-sm font-medium transition-all duration-180 cursor-pointer rounded-lg relative focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A]"
                style={{ 
                  color: '#2B2B2B', 
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14.5px',
                  fontWeight: '500'
                }}
                role="menuitem"
                onClick={() => setIsServicesOpen(false)}
              >
                How We Work
              </Link>
            </div>
          </div>
          
          <button 
            onClick={() => scrollToSection('pricing')}
            className="nav-link px-3.5 py-2 font-medium transition-all duration-180 cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A]"
            style={{ 
              color: isAtTop ? '#FFFFFF' : '#2B2B2B',
              textShadow: isAtTop ? '0 1px 2px rgba(0,0,0,0.45)' : 'none',
              fontSize: '16.5px',
              fontWeight: '500',
              letterSpacing: '0.01em',
              fontFamily: 'Inter, sans-serif',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            Pricing
          </button>
          <Link 
            to="/estimate" 
            className={`nav-link px-3.5 py-2 font-medium transition-all duration-180 cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A] ${
              isActive('/estimate') ? 'nav-link--active' : ''
            }`}
            style={{ 
              color: isAtTop ? '#FFFFFF' : '#2B2B2B',
              textShadow: isAtTop ? '0 1px 2px rgba(0,0,0,0.45)' : 'none',
              fontSize: '16.5px',
              fontWeight: '500',
              letterSpacing: '0.01em',
              fontFamily: 'Inter, sans-serif',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center'
            }}
            aria-current={isActive('/estimate') ? 'page' : undefined}
          >
            Estimate
          </Link>
          <button 
            onClick={() => scrollToSection('contact')}
            className="nav-link px-3.5 py-2 font-medium transition-all duration-180 cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A]"
            style={{ 
              color: isAtTop ? '#FFFFFF' : '#2B2B2B',
              textShadow: isAtTop ? '0 1px 2px rgba(0,0,0,0.45)' : 'none',
              fontSize: '16.5px',
              fontWeight: '500',
              letterSpacing: '0.01em',
              fontFamily: 'Inter, sans-serif',
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            Contact
          </button>
          <button 
            onClick={() => scrollToSection('contact')}
            className="cta-button ml-4 px-6 py-2.5 rounded-lg font-semibold text-white transition-all duration-180 whitespace-nowrap cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A]"
            style={{ 
              backgroundColor: '#8A6E5A',
              height: '42px',
              display: 'flex',
              alignItems: 'center',
              fontFamily: 'Inter, sans-serif'
            }}
          >
            Schedule Audit
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden p-2 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A] rounded"
          style={{ 
            color: isAtTop ? '#FFFFFF' : '#2B2B2B',
            textShadow: isAtTop ? '0 1px 2px rgba(0,0,0,0.45)' : 'none'
          }}
          aria-expanded={isMenuOpen}
          aria-label="Toggle navigation menu"
        >
          <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white shadow-lg border-t" style={{ borderColor: '#EFEAE4' }}>
          <div className="flex flex-col px-6 py-4 space-y-3.5">
            <Link 
              to="/" 
              className={`mobile-nav-link font-semibold transition-colors duration-180 cursor-pointer ${
                isActive('/') ? 'mobile-nav-link--active' : ''
              }`}
              style={{ 
                color: isActive('/') ? '#8A6E5A' : '#2B2B2B',
                fontSize: '16px',
                fontFamily: 'Inter, sans-serif'
              }}
              onClick={() => setIsMenuOpen(false)}
              aria-current={isActive('/') ? 'page' : undefined}
            >
              Home
            </Link>
            
            {/* Mobile Services */}
            <div className="space-y-2">
              <button 
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className={`mobile-nav-link font-semibold transition-colors duration-180 cursor-pointer flex items-center justify-between w-full ${
                  isActive('/services') ? 'mobile-nav-link--active' : ''
                }`}
                style={{ 
                  color: isActive('/services') ? '#8A6E5A' : '#2B2B2B',
                  fontSize: '16px',
                  fontFamily: 'Inter, sans-serif'
                }}
                aria-expanded={isServicesOpen}
                aria-current={isActive('/services') ? 'page' : undefined}
              >
                Services
                <i className={`ri-arrow-${isServicesOpen ? 'up' : 'down'}-s-line transition-transform duration-180`}></i>
              </button>
              
              {isServicesOpen && (
                <div className="pl-4 space-y-3 py-2 border-l-2" style={{ borderColor: '#EFEAE4' }}>
                  <Link 
                    to="/services/workflow-system-architecture"
                    className="block font-medium transition-colors duration-180 cursor-pointer text-sm py-1"
                    style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Workflow System Architecture
                  </Link>
                  <Link 
                    to="/services/intake-automation"
                    className="block font-medium transition-colors duration-180 cursor-pointer text-sm py-1"
                    style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Lead & Client Intake Systems
                  </Link>
                  <Link 
                    to="/services/crm-automation"
                    className="block font-medium transition-colors duration-180 cursor-pointer text-sm py-1"
                    style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    CRM Process Automation
                  </Link>
                  <Link 
                    to="/services/concierge-ai"
                    className="block font-medium transition-colors duration-180 cursor-pointer text-sm py-1"
                    style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    NextPhase Concierge AI™
                  </Link>
                  <Link 
                    to="/services/how-we-work"
                    className="block font-medium transition-colors duration-180 cursor-pointer text-sm py-1"
                    style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    How We Work
                  </Link>
                </div>
              )}
            </div>
            
            <button 
              onClick={() => {
                setIsMenuOpen(false);
                scrollToSection('pricing');
              }}
              className="mobile-nav-link font-semibold transition-colors duration-180 cursor-pointer text-left"
              style={{ 
                color: '#2B2B2B',
                fontSize: '16px',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              Pricing
            </button>
            <Link 
              to="/estimate" 
              className={`mobile-nav-link font-semibold transition-colors duration-180 cursor-pointer ${
                isActive('/estimate') ? 'mobile-nav-link--active' : ''
              }`}
              style={{ 
                color: isActive('/estimate') ? '#8A6E5A' : '#2B2B2B',
                fontSize: '16px',
                fontFamily: 'Inter, sans-serif'
              }}
              onClick={() => setIsMenuOpen(false)}
              aria-current={isActive('/estimate') ? 'page' : undefined}
            >
              Estimate
            </Link>
            <button 
              onClick={() => {
                setIsMenuOpen(false);
                scrollToSection('contact');
              }}
              className="mobile-nav-link font-semibold transition-colors duration-180 cursor-pointer text-left"
              style={{ 
                color: '#2B2B2B',
                fontSize: '16px',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              Contact
            </button>
            
            {/* Mobile CTA - Full Width */}
            <button 
              onClick={() => {
                setIsMenuOpen(false);
                scrollToSection('contact');
              }}
              className="w-full mt-4 px-6 py-3 rounded-lg font-semibold text-white transition-all duration-180 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8A6E5A]"
              style={{ 
                backgroundColor: '#8A6E5A',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              Schedule Audit
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
